import { Builder } from '@builder.io/react';
import React from 'react';

import {
  Button,
  ButtonProps,
} from '../../storyblocks-design-system/components/atoms/Button/Button';
import { Text } from '../../storyblocks-design-system/components/atoms/Text/Text';
import { BUTTON_VARIANTS, REL_VALUES } from '../../utils/constants';
import CategoryPill from '../elements/CategoryPill';

interface PillProps {
  href: string;
  label: string;
  image: string;
}

type CategoryPillsButtons = ButtonProps & {
  text: string;
  href?: string;
  mobileOnly?: boolean;
};

export interface PillsProps {
  heading: string;
  pills: PillProps[];
  showCallToAction: boolean;
  callToAction: CategoryPillsButtons;
}

export default function CategoryPills({
  heading,
  pills,
  callToAction,
  showCallToAction,
}: PillsProps) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 md:py-24 md:px-12 lg:px-24">
      {heading && (
        <Text
          fontFamily="polysans"
          fontSize="4xl"
          fontWeight="semibold"
          asChild
          className="m-0 p-0"
        >
          <h2 className="pb-8 md:pb-12 text-center">{heading}</h2>
        </Text>
      )}
      <div className="gap-x-3 gap-y-3 flex flex-row flex-wrap items-center justify-center">
        {pills.map(({ label, image, href }, index) => (
          <CategoryPill
            key={`pill-${index}-${label}`}
            href={href}
            label={label}
            image={image}
          />
        ))}
      </div>
      {showCallToAction && (
        <Button {...callToAction} className=" mt-8 md:mt-12">
          {callToAction?.text}
        </Button>
      )}
    </div>
  );
}

Builder.registerComponent(CategoryPills, {
  name: 'Category Pills',
  inputs: [
    {
      name: 'heading',
      type: 'string',
      required: true,
      defaultValue: 'Top stock footage categories',
    },
    {
      name: 'pills',
      type: 'list',
      required: true,
      defaultValue: [{ href: '/', label: 'Category Pill', image: '' }],
      subFields: [
        {
          name: 'label',
          type: 'string',
          required: true,
          defaultValue: 'Pill',
        },
        {
          name: 'image',
          type: 'file',
          allowedFileTypes: ['jpg', 'jpeg', 'png', 'webp'],
          required: true,
          defaultValue: '',
        },
        {
          name: 'href',
          type: 'string',
          required: true,
          defaultValue: '/',
        },
      ],
    },
    {
      name: 'showCallToAction',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'callToAction',
      type: 'object',
      defaultValue: {
        text: 'Browse All Categories',
        href: '/',
        variant: BUTTON_VARIANTS[1],
        size: 'large',
        target: '_self',
        rel: '',
        mobileOnly: true,
      },
      showIf: (options) => options.get('showCallToAction'),
      subFields: [
        {
          name: 'text',
          type: 'string',
          required: true,
        },
        {
          name: 'href',
          type: 'string',
          required: true,
        },
        {
          name: 'target',
          type: 'string',
          enum: ['_self', '_blank', '_parent', '_top'],
          helperText:
            'For explanation of options, see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-target',
        },
        {
          name: 'rel',
          type: 'string',
          enum: REL_VALUES,
          helperText:
            'For documentation on accepted values, see https://developer.mozilla.org/en-US/docs/Web/HTML/Link_types for link types allowed in <a> elements',
        },
        {
          name: 'variant',
          type: 'string',
          enum: BUTTON_VARIANTS,
        },
        {
          name: 'size',
          type: 'string',
          enum: ['default', 'large'],
        },
        {
          name: 'disabled',
          type: 'boolean',
          defaultValue: false,
          enum: [
            {
              label: 'Enabled',
              value: false,
            },
            {
              label: 'Disabled',
              value: true,
            },
          ],
        },
        {
          name: 'mobileOnly',
          type: 'boolean',
          defaultValue: true,
          enum: [
            {
              label: 'Enabled',
              value: true,
            },
            {
              label: 'Disabled',
              value: false,
            },
          ],
        },
      ],
    },
  ],
});
